// src/components/fraud/FraudSummaryCards.js
// Top summary cards for the Fraud Management Dashboard.
// Each card is independent; loading state shows a skeleton shimmer.

import React from 'react';
import { Container, Row } from 'react-bootstrap';

// ─── Card config ─────────────────────────────────────────────────────────────
const buildCards = (stats) => [
  {
    id: 'high-risk-today',
    title: 'High Risk Transactions',
    subtitle: 'Today',
    value: stats.highRiskToday,
    icon: '⚠️',
    accent: '#EF4444',
    accentBg: 'rgba(239,68,68,0.10)',
  },
  {
    id: 'blocked-today',
    title: 'Blocked Transactions',
    subtitle: 'Today',
    value: stats.blockedToday,
    icon: '🚫',
    accent: '#F59E0B',
    accentBg: 'rgba(245,158,11,0.10)',
  },
  {
    id: 'refunded-fraud',
    title: 'Refunded Fraud Transactions',
    subtitle: 'All time',
    value: stats.refundedFraud,
    icon: '↩️',
    accent: '#8B5CF6',
    accentBg: 'rgba(139,92,246,0.10)',
  },
  {
    id: 'suspended-users',
    title: 'Suspended Users',
    subtitle: 'Total',
    value: stats.suspendedUsers,
    icon: '🔒',
    accent: '#EF4444',
    accentBg: 'rgba(239,68,68,0.10)',
  },
  {
    id: 'fraud-logs-today',
    title: 'Fraud Logs',
    subtitle: 'Today',
    value: stats.fraudLogsToday,
    icon: '📋',
    accent: '#D9FF1F',
    accentBg: 'rgba(217,255,31,0.08)',
  },
  {
    id: 'avg-fraud-score',
    title: 'Average Fraud Score',
    subtitle: 'Based on logs',
    value: stats.avgFraudScore !== null ? `${stats.avgFraudScore}` : null,
    icon: '📊',
    accent: '#22C55E',
    accentBg: 'rgba(34,197,94,0.10)',
    suffix: '/100',
  },
];

// ─── Individual Card ──────────────────────────────────────────────────────────
const StatCard = ({ card, loading }) => (
  <div
    id={`fraud-card-${card.id}`}
    className="theme-card rounded-3 px-4 py-3 h-100"
    style={{ minHeight: 100, position: 'relative', overflow: 'hidden' }}
  >
    {/* Accent bar */}
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '3px',
        height: '100%',
        background: card.accent,
        borderRadius: '4px 0 0 4px',
      }}
    />

    {/* Icon bubble */}
    <div
      style={{
        position: 'absolute',
        top: 16,
        right: 16,
        width: 40,
        height: 40,
        borderRadius: '50%',
        background: card.accentBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
      }}
    >
      {card.icon}
    </div>

    <small
      className="body-text-three card-title-label d-block"
      style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 2 }}
    >
      {card.title}
    </small>
    <small style={{ fontSize: 10, color: 'var(--text-secondary)', opacity: 0.7 }}>
      {card.subtitle}
    </small>

    {loading ? (
      <div className="fraud-skeleton mt-2" style={{ width: '55%', height: 28, borderRadius: 6 }} />
    ) : (
      <h5
        className="text-h2 mt-2 mb-0 card-value"
        style={{ color: card.accent, fontSize: 26, fontWeight: 700, lineHeight: 1.2 }}
      >
        {card.value !== null && card.value !== undefined ? (
          <>
            {card.value}
            {card.suffix && (
              <small style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 400 }}>
                {card.suffix}
              </small>
            )}
          </>
        ) : (
          <span style={{ color: 'var(--text-secondary)', fontSize: 14, fontWeight: 400 }}>
            No data
          </span>
        )}
      </h5>
    )}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const FraudSummaryCards = ({ stats, loading }) => {
  const cards = buildCards(stats);

  return (
    <section className="py-4 nav-body-bg" style={{ marginTop: '70px' }}>
      <Container fluid className="px-4">
        <Row className="g-3">
          {cards.map((card) => (
            <div key={card.id} className="col-12 col-sm-6 col-xl-4">
              <StatCard card={card} loading={loading} />
            </div>
          ))}
        </Row>
      </Container>
    </section>
  );
};

export default FraudSummaryCards;
